import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InhouseEntryCard from './InhouseEntryCard';
import { IInhouseEntry, IInhouseWeekGroup, IInhouseCategory } from './types';

interface InhouseWeekSectionProps {
  weekGroup: IInhouseWeekGroup;
  isAdmin: boolean;
  currentUserId: string;
  lastVisitTimestamp?: string;
  onEdit: (entry: IInhouseEntry) => void;
  onDelete: (entry: IInhouseEntry) => void;
  onToggleHighlight: (entry: IInhouseEntry) => void;
  onTogglePin: (entry: IInhouseEntry) => void;
  categories?: IInhouseCategory[];
  cardSize?: 'landscape' | 'square' | 'portrait';
}

const InhouseWeekSection: React.FC<InhouseWeekSectionProps> = ({
  weekGroup,
  isAdmin,
  currentUserId,
  lastVisitTimestamp,
  onEdit,
  onDelete,
  onToggleHighlight,
  categories,
  onTogglePin,
  cardSize = 'landscape',
}) => {
  const { highlight, entries } = weekGroup;
  const regularEntries = entries.filter(e => !e.isHighlight);

  const isEntryNew = (entry: IInhouseEntry) => {
    if (!lastVisitTimestamp) return false;
    return new Date(entry.createdAt) > new Date(lastVisitTimestamp);
  };

  return (
    <Box
      sx={{
        mb: 4,
        animation: 'fadeInUp 0.4s ease-out',
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(16px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Week header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, mb: { xs: 1.5, sm: 2.5 } }}>
        <Box
          sx={{
            width: 4,
            height: { xs: 24, sm: 32 },
            bgcolor: '#072f53',
            borderRadius: 2,
          }}
        />
        <CalendarTodayIcon sx={{ color: '#072f53', fontSize: { xs: 16, sm: 20 } }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#072f53',
            letterSpacing: '-0.02em',
            fontSize: { xs: '0.9rem', sm: '1.25rem' },
          }}
        >
          {weekGroup.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
          {entries.length} {entries.length === 1 ? 'inlägg' : 'inlägg'}
        </Typography>
      </Box>

      {/* Highlight card (full width) */}
      {highlight && (
        <Box sx={{ mb: 2.5 }}>
          <InhouseEntryCard
            entry={highlight}
            isAdmin={isAdmin}
            isNew={isEntryNew(highlight)}
            currentUserId={currentUserId}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleHighlight={onToggleHighlight}
            onTogglePin={onTogglePin}
            categories={categories}
            variant="highlight"
          />
        </Box>
      )}

      {/* Regular entries grid */}
      {regularEntries.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',   // mobile: always 2 per row
              sm: 'repeat(auto-fill, 260px)', // desktop: fixed 260px
            },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {regularEntries.map(entry => (
            <InhouseEntryCard
              key={entry._id}
              entry={entry}
              isAdmin={isAdmin}
              isNew={isEntryNew(entry)}
              currentUserId={currentUserId}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleHighlight={onToggleHighlight}
              categories={categories}
              onTogglePin={onTogglePin}
              variant="normal"
              cardSize={cardSize}
            />
          ))}
        </Box>
      )}

      <Divider sx={{ mt: 3 }} />
    </Box>
  );
};

export default InhouseWeekSection;
